import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Scene } from '@babylonjs/core/scene';

import { ConfigurationRight } from '../../interfaces/ConfigurationRight';
import ArticleChildNode from '../nodes/articleChildNode';
import ArticleNode from '../nodes/articleNode';
import Settings from '../settings/settings';
import HighlightDiscontinuedLayer from './layers/highlightDiscontinuedLayer';
import HighlightLayer from './layers/highlightLayer';

export interface EventsRegistry {
  registerOnHover: (callback) => void;
  registerOnSelectedArticle: (callback) => void;
  registerOnUnselect: (callback) => void;
}

interface RegisteredEvents {
  onHover: (boolean) => void;
  onSelectedArticle: (articleId) => void;
  onUnselect: () => void;
}

export default class EventsController implements EventsRegistry {
  registerOnHover = (callback) => (this.events.onHover = callback);
  registerOnSelectedArticle = (callback) => (this.events.onSelectedArticle = callback);
  registerOnUnselect = (callback) => (this.events.onUnselect = callback);

  private events: RegisteredEvents = {
    onHover: () => {},
    onSelectedArticle: () => {},
    onUnselect: () => {},
  };

  private currentPointerTarget: ArticleNode;
  private highlight: HighlightLayer;
  private hoverHighlight: HighlightLayer;
  private discontinuedHighlight: HighlightDiscontinuedLayer;

  constructor(scene: Scene) {
    this.highlight = new HighlightLayer('select', scene);
    this.hoverHighlight = new HighlightLayer('hover', scene);
    this.discontinuedHighlight = new HighlightDiscontinuedLayer('discontinued', scene);

    scene.onPointerObservable.add((pointerInfo) => {
      switch (pointerInfo.type) {
        case PointerEventTypes.POINTERWHEEL:
        case PointerEventTypes.POINTERDOWN:
          return scene.stopAllAnimations();
        case PointerEventTypes.POINTERMOVE:
          return this.pointerMoveEventHandler(scene);
        case PointerEventTypes.POINTERTAP:
          return this.pointerClickEventHandler(scene);
      }
    });
  }

  public selectArticle(article: ArticleNode) {
    this.hoverHighlight.clearHighlights();
    this.highlight.highlight(article);
    this.discontinuedHighlight.select(article);
  }

  public markDiscontinued(article: ArticleNode) {
    this.discontinuedHighlight.addHighlight(article);
  }
  public changeRights(rights: ConfigurationRight[]) {
    const editable = rights.includes(ConfigurationRight.WRITE);
    this.discontinuedHighlight.changeColor(
      editable ? Settings.DISCONTINUED_COLOR_EDITABLE : Settings.DISCONTINUED_COLOR_READONLY,
    );
  }

  public unselect() {
    this.highlight.clearHighlights();
    this.discontinuedHighlight.unselect();
  }

  public selectedArticle(): ArticleNode {
    return this.highlight.currentHighlightedArticle;
  }

  private pointerClickEventHandler = (scene: Scene) => {
    const pickInfo = scene.pick(scene.pointerX, scene.pointerY);
    const articleNode = pickInfo.hit && this.findArticleNodeFromPickInfo(pickInfo);
    articleNode ? this.events.onSelectedArticle(articleNode.id) : this.events.onUnselect();
  };

  private pointerMoveEventHandler = (scene: Scene) => {
    const pickInfo = scene.pick(scene.pointerX, scene.pointerY);

    const articleNode = pickInfo.hit && this.findArticleNodeFromPickInfo(pickInfo);

    if (this.currentPointerTarget !== articleNode) {
      if (!articleNode) {
        this.discontinuedHighlight.hoverOut();
      } else {
        this.discontinuedHighlight.hoverIn(articleNode);
      }
      this.updateHoverHighlights(articleNode);
      this.currentPointerTarget = articleNode;
      this.events.onHover(!!articleNode);
    }
  };

  private updateHoverHighlights(articleNode: ArticleNode) {
    this.hoverHighlight.clearHighlights();
    if (articleNode && this.highlight.currentHighlightedArticle !== articleNode) {
      this.hoverHighlight.highlight(articleNode);
    }
  }

  private findArticleNodeFromPickInfo(pickInfo: PickingInfo): ArticleNode | undefined {
    const pickedArticle = pickInfo.pickedMesh.behaviors.find(
      (node) => node !== undefined,
    ) as ArticleChildNode;
    return pickedArticle?.parentArticle;
  }
}
